import React, { useCallback, useMemo } from "react";
import { Form, Select, Input, InputNumber, Checkbox, message } from "antd";
import { ShipmentIncompleteStatus } from "@/typedefs/shipment";
import {
  STATUS,
  initiatedByOptions,
  reasons,
  ShipmentStatus,
  ShipmentIncompleteInitiatedBy,
  ShipperReason,
  AutosledReason,
  TransporterReason,
} from "@/utils/enum";
import { Validator } from "@/utils/validator";
import { cancelAdminShipment, cancelAndRepostShipment } from "@/services/shipments_new";

const { TextArea } = Input;
const { Option } = Select;

export default function CancelShipmentForm({
  formRef,
  shipmentId,
  afterSubmit,
  setStatus,
  shipmentStatus,
  isCancelAndRepost,
}) {
  const onSubmit = useCallback(
    data => {
      setStatus(STATUS.LOADING);

      const isShipmentPosted = shipmentStatus === ShipmentStatus.Posted;

      const payload = {
        ...data,
        ...(isShipmentPosted && {
          initiatedBy: ShipmentIncompleteInitiatedBy.Autosled,
          status: ShipmentIncompleteStatus.CANCELLED,
        }),
      };

      const apiCaller = isCancelAndRepost ? cancelAndRepostShipment : cancelAdminShipment;

      apiCaller(shipmentId, payload)
        .then(() => {
          setStatus(STATUS.SUCCESS);
          if (isCancelAndRepost) {
            message.success("Load was cancelled and shipment is now available for reassignment.");
          }

          afterSubmit?.();
        })
        .catch(error => {
          setStatus(STATUS.ERROR);
          console.error(error);
        });
    },
    [afterSubmit, setStatus, shipmentId, shipmentStatus, isCancelAndRepost],
  );

  const isShipmentAssigned = useMemo(() => shipmentStatus === ShipmentStatus.Assigned, [shipmentStatus]);

  const initiatorOptions = useMemo(() => {
    if (shipmentStatus === ShipmentStatus.Posted) {
      return initiatedByOptions.filter(option =>
        [ShipmentIncompleteInitiatedBy.Autosled, ShipmentIncompleteInitiatedBy.Dealer].includes(option.value),
      );
    }

    if (shipmentStatus === ShipmentStatus.Assigned && !isCancelAndRepost) {
      return initiatedByOptions.filter(option =>
        [ShipmentIncompleteInitiatedBy.Autosled, ShipmentIncompleteInitiatedBy.Dealer].includes(option.value),
      );
    }

    if (shipmentStatus === ShipmentStatus.Assigned && isCancelAndRepost) {
      return initiatedByOptions.filter(option =>
        [ShipmentIncompleteInitiatedBy.Autosled, ShipmentIncompleteInitiatedBy.Transporter].includes(option.value),
      );
    }

    return initiatedByOptions;
  }, [shipmentStatus, isCancelAndRepost]);

  return (
    <Form layout="vertical" form={formRef} onFinish={onSubmit}>
      <Form.Item name="initiatedBy" label="Initiated By" rules={[Validator.required("Please select an initiator")]}>
        <Select placeholder="Initiator">
          {initiatorOptions.map(option => (
            <Option key={option.value} value={option.value}>
              <span style={{ color: "#000", fontWeight: 600 }}>{option.title}</span>
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item dependencies={["initiatedBy"]} noStyle>
        {({ getFieldValue }) => {
          const initiatedBy = getFieldValue("initiatedBy");

          return (
            <Form.Item name="reason" label="Reason" rules={[Validator.required("Please select a reason")]}>
              <Select placeholder="Reason">
                {reasons[initiatedBy]?.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.title}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.reason !== currentValues.reason} noStyle>
        {({ getFieldValue }) => {
          const reason = getFieldValue("reason");
          const initiatedBy = getFieldValue("initiatedBy");

          const otherReasonMap = {
            [ShipmentIncompleteInitiatedBy.Autosled]: AutosledReason.Other,
            [ShipmentIncompleteInitiatedBy.Dealer]: ShipperReason.Other,
            [ShipmentIncompleteInitiatedBy.Transporter]: TransporterReason.Other,
          };

          return reason && reason === otherReasonMap[initiatedBy] ? (
            <Form.Item
              name="otherReason"
              label="Other Reason"
              rules={[Validator.required("Please give another reason")]}
            >
              <TextArea rows={4} />
            </Form.Item>
          ) : null;
        }}
      </Form.Item>

      {isShipmentAssigned && (
        <Form.Item name="isDryRunApproved" valuePropName="checked">
          <Checkbox>Dry Run Approved?</Checkbox>
        </Form.Item>
      )}

      <Form.Item dependencies={["isDryRunApproved"]} noStyle>
        {({ getFieldValue }) => {
          const isDryRunApproved = getFieldValue("isDryRunApproved");
          if (!isDryRunApproved) return null;

          return (
            <>
              <Form.Item
                name="dryRunPrice"
                label="Shipper Dry Run Fee"
                rules={[Validator.number("Please enter a valid price")]}
              >
                <InputNumber min={0} defaultValue={0} />
              </Form.Item>
              <Form.Item
                name="dryRunCost"
                label="Transporter Dry Run Fee"
                rules={[Validator.number("Please enter a valid cost")]}
              >
                <InputNumber min={0} defaultValue={0} />
              </Form.Item>
            </>
          );
        }}
      </Form.Item>

      <Form.Item name="notes" label="Notes" rules={[Validator.required("Please enter notes")]}>
        <TextArea rows={4} />
      </Form.Item>

      {isShipmentAssigned && isCancelAndRepost && (
        <Form.Item name="setShipmentToPending" valuePropName="checked">
          <Checkbox>Set Shipment to Pending?</Checkbox>
        </Form.Item>
      )}
    </Form>
  );
}
